import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useFormik } from "formik";
import { Box, CircularProgress, Typography } from "@mui/material";
import { BaseFormPage } from "../../components/common/BaseForm";
import {
  useGetClaimByIdQuery,
  useUpdateClaimMutation,
} from "../../api/claimsApi";
import { useFormSubmission } from "../../hooks/useFormSubmission";
import ClaimFormLayout from "./components/ClaimFormLayout";
import { PendingFile } from "./components/ClaimPendingFilesSection";
import {
  claimValidationSchema,
  getInitialClaimFormValues,
  ClaimFormValues,
  computeTotalEstimate,
  parseEstimate,
} from "./validation/claimFormValidation";
import { UpdateClaimDto } from "../../types/claim";

const ClaimEditPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const claimId = Number(id);

  const {
    data: claim,
    isLoading: isLoadingClaim,
    error: loadError,
  } = useGetClaimByIdQuery(claimId, { skip: !claimId });

  const [updateClaim] = useUpdateClaimMutation();
  const [pendingFiles, setPendingFiles] = useState<PendingFile[]>([]);
  const [deletedDocIds, setDeletedDocIds] = useState<number[]>([]);
  const [docsInitialized, setDocsInitialized] = useState(false);

  useEffect(() => {
    if (claim?.documents && !docsInitialized) {
      const existing: PendingFile[] = claim.documents.map((doc) => ({
        id: `existing-${doc.id}`,
        existingDocId: doc.id,
        fileName: doc.originalFileName,
        fileSize: doc.fileSizeBytes,
        documentCategory: doc.documentCategory || "",
        url: doc.url,
        contentType: doc.contentType,
      }));
      setPendingFiles(existing);
      setDocsInitialized(true);
    }
  }, [claim, docsInitialized]);

  const handlePendingFilesChange = (updated: PendingFile[]) => {
    const removed = pendingFiles
      .filter((pf) => pf.existingDocId && !updated.find((u) => u.id === pf.id))
      .map((pf) => pf.existingDocId!);
    if (removed.length > 0) {
      setDeletedDocIds((prev) => [...new Set([...prev, ...removed])]);
    }
    setPendingFiles(updated);
  };

  const {
    isSubmitting,
    submitError,
    successMessage,
    showValidationErrors,
    handleSubmit,
  } = useFormSubmission({
    successMessage: "Claim updated successfully!",
    onSuccess: () => {
      setTimeout(() => navigate("/claims"), 1500);
    },
  });

  const initialValues: ClaimFormValues = claim
    ? {
        dateOfLoss: claim.dateOfLoss
          ? new Date(claim.dateOfLoss).toISOString().split("T")[0]
          : "",
        city: claim.city || "",
        state: claim.state || "",
        claimCompanyId: claim.claimCompanyId,
        claimCompanyName: null,
        assignedToUserId: claim.assignedToUserId,
        driverEmployeeId: claim.driverEmployeeId,
        vehicleId: claim.vehicleId,
        trailerId: claim.trailerId,
        description: claim.description || "",
        claimNumber: claim.claimNumber || "",
        note: claim.note || "",
        adjusterName: claim.adjusterName || "",
        adjusterPhone: claim.adjusterPhone || "",
        adjusterEmail: claim.adjusterEmail || "",
        autoLB: claim.autoLB,
        policeReportDescription: claim.policeReportDescription || "",
        legacyTotalEstimate:
          claim.truckEstimate == null && claim.trailerEstimate == null
            ? claim.totalEstimate
            : null,
        truckEstimate: claim.truckEstimate ?? "",
        trailerEstimate: claim.trailerEstimate ?? "",
        status: claim.status,
        driverCharged: claim.driverCharged,
      }
    : getInitialClaimFormValues();

  const formik = useFormik<ClaimFormValues>({
    initialValues,
    enableReinitialize: true,
    validationSchema: claimValidationSchema,
    onSubmit: async (values) => {
      await handleSubmit(async () => {
        const dto: UpdateClaimDto = {
          dateOfLoss: values.dateOfLoss,
          city: values.city || null,
          state: values.state || null,
          claimCompanyId: values.claimCompanyId,
          claimCompanyName: values.claimCompanyName,
          assignedToUserId: values.assignedToUserId,
          driverEmployeeId: values.driverEmployeeId,
          vehicleId: values.vehicleId,
          trailerId: values.trailerId,
          description: values.description || null,
          claimNumber: values.claimNumber || null,
          note: values.note || null,
          adjusterName: values.adjusterName || null,
          adjusterPhone: values.adjusterPhone || null,
          adjusterEmail: values.adjusterEmail || null,
          autoLB: values.autoLB,
          policeReportDescription: values.policeReportDescription || null,
          truckEstimate: parseEstimate(values.truckEstimate),
          trailerEstimate: parseEstimate(values.trailerEstimate),
          totalEstimate: computeTotalEstimate(
            values.truckEstimate,
            values.trailerEstimate,
            values.legacyTotalEstimate
          ),
          status: values.status,
          driverCharged: values.driverCharged,
        };

        const newFiles = pendingFiles.filter((pf) => pf.file);
        const files = newFiles.map((pf) => pf.file!);
        const fileCategories = newFiles.map((pf) => pf.documentCategory || "");

        return await updateClaim({
          id: claimId,
          dto,
          files,
          fileCategories,
          deletedDocumentIds: deletedDocIds,
        }).unwrap();
      });
    },
  });

  if (isLoadingClaim) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (loadError || !claim) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography color="error">
          Failed to load claim. Please try again.
        </Typography>
      </Box>
    );
  }

  return (
    <BaseFormPage
      title="Edit Claim"
      subtitle={`Editing claim ${claim.claimNumber || `#${claim.id}`}`}
      onBack={() => navigate("/claims")}
      formik={formik}
      isSubmitting={isSubmitting}
      successMessage={successMessage}
      errorMessage={submitError}
      showValidationErrors={showValidationErrors}
      submitButtonText="Save Changes"
      maxWidth="lg"
    >
      <ClaimFormLayout
        mode="edit"
        claimId={claimId}
        availableCompanies={claim.availableCompanies || []}
        availableDrivers={claim.availableDrivers || []}
        availableSafetyUsers={claim.availableSafetyUsers || []}
        availableTrucks={claim.availableTrucks || []}
        availableTrailers={claim.availableTrailers || []}
        pendingFiles={pendingFiles}
        onPendingFilesChange={handlePendingFilesChange}
      />
    </BaseFormPage>
  );
};

export default ClaimEditPage;
